import { useState } from 'react';
import { Box, ButtonBase } from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import { ChartCard } from './ChartCard';
import { useMeasuredWidth, niceTicks } from './chartScale';

export interface StackedBarSeries {
  id: string;
  label: string;
  /** Concrete fill color (e.g. `theme.palette.chart[i]`). */
  color: string;
  /** One value per x-label; stacked bottom → top in array order. */
  values: number[];
}

export interface StackedBarOverlay {
  label: string;
  /** One value per x-label; drawn as a line on a secondary right axis. */
  values: number[];
  color?: string;
}

export interface StackedBarChartProps {
  title: string;
  /** X-axis labels (one per period). */
  labels: string[];
  series: StackedBarSeries[];
  /** Optional right-axis line overlay (e.g. line count). */
  overlay?: StackedBarOverlay;
  height?: number;
  /** Left-axis tick formatter (defaults to a locale integer). */
  formatValue?: (value: number) => string;
  /** Right-axis (overlay) tick formatter. */
  formatOverlay?: (value: number) => string;
}

function SeriesToggle({
  color,
  label,
  kind = 'box',
  active,
  onClick,
}: {
  color: string;
  label: string;
  kind?: 'box' | 'line';
  active: boolean;
  onClick: () => void;
}) {
  const { mode, systemMode } = useColorScheme();
  const isDark = (mode === 'system' ? systemMode : mode) === 'dark';
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: 999,
        bgcolor: active
          ? isDark
            ? 'rgba(255,255,255,0.06)'
            : 'rgba(4,29,51,0.04)'
          : 'transparent',
        border: '1px solid',
        borderColor: active ? 'divider' : 'transparent',
        color: active ? 'text.primary' : 'text.secondary',
        fontSize: 12,
        fontWeight: 500,
        opacity: active ? 1 : 0.55,
        transition: 'opacity 120ms, background 120ms',
      }}
    >
      {kind === 'box' && (
        <Box component="span" sx={{ width: 10, height: 10, bgcolor: color, borderRadius: '2px' }} />
      )}
      {kind === 'line' && (
        <Box
          component="span"
          sx={{ width: 14, height: '2px', bgcolor: color, borderRadius: '1px' }}
        />
      )}
      {label}
    </ButtonBase>
  );
}

/**
 * Per-period stacked-bar chart with a togglable per-series legend and an
 * optional right-axis line overlay (e.g. line count). Series can be switched
 * on/off from the header chips (never all off). Self-contained: renders its own
 * {@link ChartCard} so the interactive legend lives in the header. Port of the
 * prototype `ExpenseChart`. On the dashboard it carries a single "Total spend"
 * series; `10-reports` composes it with the 4 expense categories.
 */
export function StackedBarChart({
  title,
  labels,
  series,
  overlay,
  height = 250,
  formatValue,
  formatOverlay,
}: StackedBarChartProps) {
  const { mode, systemMode } = useColorScheme();
  const isDark = (mode === 'system' ? systemMode : mode) === 'dark';
  const [ref, width] = useMeasuredWidth();
  const [enabled, setEnabled] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(series.map((s) => [s.id, true])),
  );
  const [showOverlay, setShowOverlay] = useState(Boolean(overlay));

  const fmtL = formatValue ?? ((v: number) => v.toLocaleString());
  const fmtR = formatOverlay ?? ((v: number) => v.toLocaleString());

  const toggle = (id: string) =>
    setEnabled((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      return Object.values(next).some(Boolean) ? next : prev;
    });

  const visible = series.filter((s) => enabled[s.id]);
  const n = labels.length;

  const totals = Array.from({ length: n }, (_, i) =>
    visible.reduce((sum, s) => sum + (s.values[i] ?? 0), 0),
  );
  const { ticks, niceMax } = niceTicks(Math.max(1, ...totals), 5);
  const overlayMax = overlay ? Math.max(1, ...overlay.values) : 1;
  const { ticks: rightTicks, niceMax: rightNiceMax } = niceTicks(overlayMax, 4);

  const padL = 64;
  const padR = showOverlay && overlay ? 56 : 16;
  const padT = 12;
  const padB = 28;
  const innerW = Math.max(0, width - padL - padR);
  const innerH = height - padT - padB;
  const stepX = n > 0 ? innerW / n : innerW;
  const barW = Math.max(3, stepX * 0.66);
  const y = (v: number) => padT + innerH - (v / niceMax) * innerH;
  const y2 = (v: number) => padT + innerH - (v / rightNiceMax) * innerH;

  const grid = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(4,29,51,0.06)';
  const axis = isDark ? 'rgba(255,255,255,0.65)' : '#435666';
  const overlayColor = overlay?.color ?? (isDark ? '#FFFFFF' : '#041D33');
  const dotStroke = isDark ? '#142639' : '#FFFFFF';

  const overlayPts = overlay
    ? overlay.values.map((v, i) => [padL + i * stepX + stepX / 2, y2(v)] as const)
    : [];
  const overlayPath = overlayPts
    .map(([x, yy], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${yy.toFixed(1)}`)
    .join(' ');

  const legend = (
    <>
      {series.map((s) => (
        <SeriesToggle
          key={s.id}
          color={s.color}
          label={s.label}
          active={!!enabled[s.id]}
          onClick={() => toggle(s.id)}
        />
      ))}
      {overlay && (
        <SeriesToggle
          kind="line"
          color={overlayColor}
          label={overlay.label}
          active={showOverlay}
          onClick={() => setShowOverlay((v) => !v)}
        />
      )}
    </>
  );

  return (
    <ChartCard title={title} legend={legend} height={height}>
      <Box ref={ref} sx={{ width: '100%', height: '100%' }}>
        {width > 0 && n > 0 && (
          <svg width={width} height={height} style={{ display: 'block', fontFamily: 'inherit' }}>
            {ticks.map((t, i) => {
              const yy = y(t);
              return (
                <g key={i}>
                  <line
                    x1={padL}
                    y1={yy}
                    x2={padL + innerW}
                    y2={yy}
                    stroke={grid}
                    strokeDasharray={i === 0 ? undefined : '2 4'}
                  />
                  <text
                    x={padL - 8}
                    y={yy + 4}
                    textAnchor="end"
                    fontSize="11"
                    fill={axis}
                    fontWeight="500"
                  >
                    {fmtL(t)}
                  </text>
                </g>
              );
            })}

            {showOverlay &&
              overlay &&
              rightTicks.map((t, i) => (
                <text
                  key={`r${i}`}
                  x={padL + innerW + 8}
                  y={y2(t) + 4}
                  textAnchor="start"
                  fontSize="11"
                  fill={axis}
                  fontWeight="500"
                >
                  {fmtR(t)}
                </text>
              ))}

            {Array.from({ length: n }, (_, i) => {
              const cx = padL + i * stepX + stepX / 2;
              let cumulative = 0;
              return (
                <g key={i}>
                  {visible.map((s, segIdx) => {
                    const v = s.values[i] ?? 0;
                    if (v <= 0) return null;
                    const segTop = y(cumulative + v);
                    const segBot = y(cumulative);
                    const isTop =
                      segIdx === visible.length - 1 ||
                      visible.slice(segIdx + 1).every((k) => (k.values[i] ?? 0) <= 0);
                    cumulative += v;
                    return (
                      <rect
                        key={s.id}
                        x={cx - barW / 2}
                        y={segTop}
                        width={barW}
                        height={Math.max(0, segBot - segTop) - (isTop ? 0 : 1)}
                        fill={s.color}
                        rx={isTop ? 2 : 0}
                        ry={isTop ? 2 : 0}
                      />
                    );
                  })}
                </g>
              );
            })}

            {showOverlay && overlay && (
              <>
                <path d={overlayPath} fill="none" stroke={overlayColor} strokeWidth="2" />
                {overlayPts.map(([x, yy], i) => (
                  <circle
                    key={i}
                    cx={x}
                    cy={yy}
                    r={i === n - 1 ? 3.5 : 2.25}
                    fill={overlayColor}
                    stroke={dotStroke}
                    strokeWidth="1"
                  />
                ))}
              </>
            )}

            {labels.map((m, i) => (
              <text
                key={i}
                x={padL + i * stepX + stepX / 2}
                y={height - 8}
                textAnchor="middle"
                fontSize="11"
                fill={axis}
                fontWeight="500"
              >
                {m}
              </text>
            ))}
          </svg>
        )}
      </Box>
    </ChartCard>
  );
}
